import React, { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { createContext } from "@compono/ui";
import {
  AuthProvider as OidcAuthProvider,
  hasAuthParams,
  useAuth,
} from "react-oidc-context";
import { User, UserManager } from "oidc-client-ts";
import { useRouteOrgId } from "~/hooks/useRouteOrgId";
import { env } from "~/env";
import { extractOrgIdFromPath } from "../RouteOrgIdProvider";

export type AuthenticationProviderProps = {
  children: React.ReactNode;
  userManager?: UserManager;
  onRedirectCallback?: (appState?: User | undefined) => void;
  autoSignin?: boolean;
};

/*
  AuthenticationProvider is a wrapper around AuthProvider
  We use it:
    - to configure Auth
    - as a facade to Auth to decouple our code from identity provider
    - customise the data format available via our useAuthentication hook
*/
export const AuthenticationProvider = (props: AuthenticationProviderProps) => {
  const {
    children,
    userManager,
    onRedirectCallback,
    autoSignin = true,
  } = props;
  const history = useHistory();
  /**
   * Take users back to the route they intended to access before authentication
   */
  const defaultOnRedirectCallback = (user: User | undefined) => {
    history.push(userReturnToDestination(user) || window.location.pathname);
  };

  return (
    <OidcAuthProvider
      {...(userManager
        ? { userManager }
        : {
            authority: env.AUTH_GATEWAY_OIDC_ISSUER,
            client_id: env.AUTH_CLIENT_ID,
            redirect_uri: window.location.origin,
            scope: "openid email profile read:all",
            // resource="compono-api"
          })}
      onSigninCallback={onRedirectCallback || defaultOnRedirectCallback}
    >
      <AutoSignin enabledAutoSignin={autoSignin}>{children}</AutoSignin>
    </OidcAuthProvider>
  );
};

const userReturnToDestination = (user: User | undefined | null) => {
  if (
    !user ||
    !user.state ||
    !(typeof user.state === "object") ||
    !("returnTo" in user.state) ||
    !(typeof user.state.returnTo === "string")
  ) {
    return undefined;
  }

  return user.state.returnTo || undefined;
};

type AutoSigninContext = {
  isAutoSigningIn?: boolean;
  hasAutoSignedIn?: boolean;
  shouldAutoSignin?: boolean;
};

const [useAutoSignin, AutoSigninContext] = createContext<AutoSigninContext>({
  name: "AutoSignin",
});

/**
 * This component automatically tries to sign in the user when the app loads
 * to renew the tokens if the user is already signed in.
 * This is to ensure that latest permissions are loaded as it would be used to determine the orgId
 */
const AutoSignin = ({
  children,
  enabledAutoSignin,
}: PropsWithChildren<{
  enabledAutoSignin?: boolean;
}>) => {
  const auth = useAuth();
  // detect if we are in an iframe
  // silent signin happens in an iframe so we don't want to do auto signin there
  const isInIframe = window.self !== window.top;

  // we only want to do silent signin once when the app loads
  const [shouldAutoSignin, setShouldAutoSignin] = useState(
    !isInIframe && enabledAutoSignin
  );
  const [signingIn, setSigningIn] = useState(false);
  const [hasAutoSignedIn, setHasAutoSignedIn] = useState(false);

  // if we have auth params in the url (just signed in), we should not do silent signin
  useEffect(() => {
    if (hasAuthParams()) {
      setShouldAutoSignin(false);
    }
  }, [auth]);

  // automatically try to sign in when app loads to renew tokens
  useEffect(() => {
    if (
      !isInIframe && // don't need to do this in iframe (silent signin)
      !signingIn && // don't need to do this if we are already auto signing in
      !auth.activeNavigator &&
      !auth.isLoading &&
      shouldAutoSignin
    ) {
      setSigningIn(true);
      auth
        .signinSilent()
        .then(() => setShouldAutoSignin(false))
        .finally(() => {
          setSigningIn(false);
          setHasAutoSignedIn(true);
        });
    }
  }, [auth, shouldAutoSignin, isInIframe]);

  // automatically renew token a bit before it expires
  useEffect(() => {
    return auth.events.addAccessTokenExpiring(() => {
      auth.signinSilent();
    });
  }, [auth.events, auth.signinSilent]);

  if (isInIframe) {
    // prevent further rendering in iframe (silent signin)
    return null;
  }

  return (
    <AutoSigninContext.Provider
      value={{
        isAutoSigningIn: signingIn,
        shouldAutoSignin,
        hasAutoSignedIn,
      }}
    >
      {children}
    </AutoSigninContext.Provider>
  );
};

type UserAppData = {
  id: string;
  accesses: {
    organisations: { organisationId: string; deactivated?: boolean }[];
  };
};

/*
  copied from services/shortlyster/service/src/utils/user.ts getActiveOrgs
  which is used by the `currentOrganisation` resolver in services/shortlyster/service/src/modules/organisations/resolvers.ts
*/
const getActiveOrgs = (user: UserAppData) =>
  user?.accesses?.organisations
    ?.filter((org) => org.deactivated !== true)
    ?.filter(Boolean) || [];

type GetCurrentOrgIdArg = {
  requestedOrgId?: string;
  userAppData: UserAppData;
};

/*
  getCurrentOrgId returns an organisation id to use in data requests.

  If the org id specified in the url is valid then we use that, otherwise we
  use the first org id available from userAppData.
*/
const getCurrentOrgId = (arg: GetCurrentOrgIdArg) => {
  const { requestedOrgId, userAppData } = arg;

  const orgIds = getActiveOrgs(userAppData).map(
    (org: { organisationId: string }) => org.organisationId
  );

  const defaultOrgId = orgIds?.[0] || false;
  const requestedOrgIdIsValid =
    requestedOrgId && orgIds.includes(requestedOrgId);

  return requestedOrgIdIsValid ? requestedOrgId : defaultOrgId;
};

export const useAuthentication = () => {
  const auth = useAuth();
  const {
    isAuthenticated,
    isLoading,
    user,
    signinRedirect,
    signoutRedirect,
    signinSilent,
  } = auth;

  const { hasAutoSignedIn, shouldAutoSignin, isAutoSigningIn } =
    useAutoSignin();

  // user might be redirecting back after signin with returnTo state
  // we need to take that into account when determining the requested org id
  const returnTo = userReturnToDestination(user);
  const { orgId: orgIdFromRoute } = useRouteOrgId();
  const requestedOrgId = returnTo
    ? extractOrgIdFromPath(returnTo)
    : orgIdFromRoute;

  // feels like this "url" should come from env/config
  const userAppData = useMemo(
    () => user?.profile["https://shortlyster.com/app_metadata"],
    [user]
  ) as UserAppData | undefined;

  // determine orgId
  // - undefined: we are waiting for auto signin to complete
  // - false: no valid orgId
  const orgId = useMemo(() => {
    // don't return orgId if we are about to auto signin, to wait for the updated userAppData
    // also not returning orgId unless user is authenticated
    if (shouldAutoSignin && !hasAutoSignedIn && auth.isAuthenticated) {
      return undefined;
    }

    return userAppData
      ? getCurrentOrgId({ requestedOrgId, userAppData })
      : false;
  }, [
    requestedOrgId,
    userAppData?.accesses,
    hasAutoSignedIn,
    shouldAutoSignin,
    auth.isAuthenticated,
  ]);

  const getAccessTokenSilently = async () => {
    if (user) {
      return user.access_token;
    }

    const signedInUser = await signinSilent();
    if (signedInUser) {
      return signedInUser.access_token;
    }

    return null;
  };

  return {
    getAccessTokenSilently,
    logout: signoutRedirect,
    loginWithRedirect: signinRedirect,
    isAuthenticated,
    isAutoSigningIn,
    isLoading,
    user: user ? { id: userAppData?.id } : undefined, // deprecate?
    userId: userAppData?.id,
    orgId,
  };
};
